import { MprcRequestErrorDialog, MprcRequestIndicator } from "components";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { UserInfoDto, UserInfoDtoPaginatedList, UserSettingsDto } from "generated/CoreApi_v1";
import { enqueueSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BatchEditSearchUsers, IBatchOperationFilter } from "views/foundation/user/components/BatchEdit/BatchEditSearchUsers";
import { useUserBatchEditContext } from "views/foundation/user/Home/BatchEdit";
import { FilterCmd } from "views/general/AdvancedFilter/FilterCmd";
import { OPERATION_PAGE_SIZE } from "views/lms/enrollment/Home";

////////////////////////////////////////////////////////////////////////////

export const OperationSearchUsers: React.FC = () => {

    const ctx = useUserBatchEditContext();
    const { t } = useTranslation();
    const api = useCoreApi();

    const [viewError, setViewError] = useState<any>();
    const [viewData, setViewData] = useState<UserInfoDtoPaginatedList>();
    const [loading, setLoading] = useState<boolean>(false);
    const [posting, setPosting] = useState<boolean>(false);
    const [viewFilter, setViewFilter] = useState<IBatchOperationFilter>({
        pageNumber: 1,
        filter: {
            searchText: "",
            advFilters: [],
            order: null
        },
    });

    ///
    const resetFilter = () => {
        setViewFilter({
            pageNumber: 1,
            filter: {
                searchText: "",
                advFilters: [],
                order: null
            },
        });
    };

    const buildFilter = {
        operation: ctx.operation!,
        filter: viewFilter.filter.advFilters,
        searchText: viewFilter.filter.searchText,
        useImportedUsers: ctx.isImportedFile,
        action: (ctx.options.tag?.action ?? ctx.options.group?.action ?? ctx.options.role?.action ?? ctx.options.topic?.action ?? ctx.options.unit?.action) ?? null,
        objectId: (ctx.options.tag?.tagId ?? ctx.options.topic?.topicId ?? ctx.options.group?.groupId ?? ctx.options.role?.roleId ?? ctx.options.unit?.unitId) ?? null,
    };

    ///
    const addAllUsers = () => {
        setPosting(false);
        api.v1.userOperation.addAllUsers({ ...buildFilter })
            .then(response => {
                if (response.data) {
                    ctx.setReloadSelectedUsers(true);
                }
                else {
                    enqueueSnackbar(t("User.BatchEdit.OperationFailMessage"), { variant: "error" });
                }
            })
            .catch(e => MprcRequestSnackbarErrorReport(e))
            .finally(() => setPosting(false));
    };

    ///
    const addUser = (item: UserInfoDto) => {
        setPosting(true);
        api.v1.userOperation.addUser({
            operation: ctx.operation!,
            userIds: [item.userId]
        }).then(response => {
            if (response.data) {
                ctx.setReloadSelectedUsers(true);
            }
            else {
                enqueueSnackbar(t("User.BatchEdit.OperationFailMessage"), { variant: "error" });
            }
        }).catch(e => MprcRequestSnackbarErrorReport(e))
            .finally(() => setPosting(false));
    };

    ///
    const clearImportedUser = (onSuccess?: () => void) => {
        setPosting(true);

        api.v1.userOperation.clearImportedUsers({
            operation: ctx.operation!,
        }).then(result => {
            if (result.data) {
                ctx.setIsImportedFile(false);
                ctx.setReloadSearchUsers(true);
            }
            else {
                enqueueSnackbar(t("User.BatchEdit.OperationFailMessage"), { variant: "error" });
            }
        }).catch(e => {
            MprcRequestSnackbarErrorReport(e);
        }).finally(() => setPosting(false));
    };

    const getData = (pageSize?: number) => {
        if (!ctx.operation) return;

        api.v1.userOperation.getSelectableUsers({
            ...buildFilter,
            pageNumber: viewFilter.pageNumber,
            pageSize: pageSize ?? OPERATION_PAGE_SIZE,
        }).then(response => {
            setViewData(response.data);
        })
            .catch(e => {
                setViewData(undefined);
                setViewError(e);
            })
            .finally(() => {
                setLoading(false);
                ctx.setReloadSearchUsers(false);
            });
    };

    ///
    useEffect(() => {
        if (ctx.reloadSearchUsers && ctx.operation != undefined) {
            resetFilter();
            getData();
        }
    }, [ctx.reloadSearchUsers]);

    /////
    useEffect(() => {
        getData();
    }, [viewFilter, ctx.options]);


    ///
    const handleOnCompleteImport = (success: boolean) => {
        if (success) {
            ctx.setReloadSelectedUsers(true);
            ctx.setIsImportedFile(success);
            ctx.setReloadSearchUsers(true);
        }
    };


    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////


    return (
        <>
            <MprcRequestIndicator visible={loading || posting} type={posting ? "post" : "request"} />
            <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

            <BatchEditSearchUsers
                viewData={viewData}
                onAddUser={addUser}
                onAddAllUsers={addAllUsers}
                onClearImportedUsers={clearImportedUser}
                viewFilter={viewFilter}
                importedFile={ctx.isImportedFile}
                onChangeFilter={filter => setViewFilter(state => ({ ...state, ...filter }))}
                onResetFilter={resetFilter}
                onImportList={users => api.v1.userOperation.importUserList({
                    operation: ctx.operation!,
                    filtered: ctx.isImportedFile,
                    userAuthFieldValues: users
                })}
                onImportFile={fileId => api.v1.userOperation.importUserFile({
                    operation: ctx.operation!,
                    filtered: ctx.isImportedFile,
                    fileId: fileId
                })}
                onCompleteImport={handleOnCompleteImport}
                filterCommands={[
                    ...FilterCmd.User.unitType(ctx.settings.unitTypes, 3),
                    FilterCmd.User.group(4),
                    FilterCmd.User.channelMember(4),
                    FilterCmd.User.topicOptional(4),
                    FilterCmd.User.topicRequired(4),

                    FilterCmd.User.trainingApproved(5),
                    FilterCmd.User.trainingReproved(5),
                    FilterCmd.User.trainingAttending(5),

                    FilterCmd.User.isManager(6),
                    FilterCmd.User.managerDirectSubordinates(6),
                    FilterCmd.User.managerAllSubordinates(6),

                    FilterCmd.User.workplace(7, { workplaceLabel: ctx.settings.workplaceLabel, isWorkplaceFreeText: ctx.settings.isWorkplaceFreeText } as UserSettingsDto),
                    FilterCmd.User.tag(7),
                    FilterCmd.User.interestTag(7),
                    FilterCmd.User.customField(7),

                    FilterCmd.User.internals(8),
                    FilterCmd.User.externals(8),
                    FilterCmd.User.blockReason(8),
                    FilterCmd.User.onlyInactives(8),
                ]}
            />
        </>
    );
};
